import React from "react";
import { Box, List, ListItemButton, ListItemText, Pagination, Skeleton, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { ScoreApi } from '../api/ScoreApi';
import AppBarContext from '../context/AppBarContext';
import { handleErrorMessage } from './ErrorDialog';
import { useHistory, useLocation } from 'react-router-dom';

export default function UserLeaderboard() {
    const history = useHistory();
    const location = useLocation();
    const { setErrorMessage } = React.useContext(AppBarContext);

    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);

    const [userAverages, setUserAverages] = React.useState<{ name: string; avgScore: number }[]>([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const itemsPerPage = 10;

    React.useEffect(() => {
        const skeletonTimeout = setTimeout(() => setShowSkeleton(true), 300);

        ScoreApi.getAverageScoreByUser()
            .then(res => {
                if (res.status >= 300) throw new Error("Failed fetching average scores");
                setUserAverages(res.data);
            })
            .catch(err => handleErrorMessage(err, setErrorMessage, history, location))
            .finally(() => setIsLoading(false));

        return () => clearTimeout(skeletonTimeout);
    }, [history, location, setErrorMessage]);

    const paginatedList = userAverages.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const pageCount = Math.ceil(userAverages.length / itemsPerPage);

    const handleUserClick = (userName: string) => {
        // Navigate to the UserSentenceScore page (adjust route as needed)
        history.push(`/user_sentence_score/${encodeURIComponent(userName)}`);
    };

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

            <Typography variant="h2" sx={{ color: '#002A6E', mb: 3, textAlign: 'center' }}>
                User Leaderboard
            </Typography>

            {isLoading && showSkeleton ? (
                <Stack spacing={2} sx={{ width: '100%' }}>
                    {Array.from({ length: itemsPerPage }).map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={36} width="100%" sx={{ borderRadius: 1 }} />
                    ))}
                </Stack>
            ) : (
                <Box sx={{ width: '100%', px: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', }} >
                    <List sx={{ width: '100%' }}>
                        {paginatedList.map((user, index) => (
                            <ListItemButton 
                                key={user.name} 
                                onClick={() => handleUserClick(user.name)} 
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2 }}
                            >
                                <Typography sx={{ fontWeight: 'bold', width: 24, textAlign: 'center' }}>
                                    {(currentPage - 1) * itemsPerPage + index + 1}
                                </Typography>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                            {user.name}
                                        </Typography>
                                    }
                                />
                                <Typography variant="h6">
                                    {user.avgScore.toFixed(2)}
                                </Typography>

                                <ChevronRightRoundedIcon sx={{ color: 'gray' }} />
                            </ListItemButton>
                        ))}
                    </List>

                    {/* Pagination must not depend on `showSkeleton`: fast API can return before the
                        300ms skeleton delay, leaving showSkeleton false and hiding pages (users past
                        the first 10 never appear in the DOM). */}
                    {!isLoading && pageCount > 1 && (
                        <Pagination
                            count={pageCount}
                            page={currentPage}
                            onChange={(_, value) => setCurrentPage(value)}
                            color="primary"
                            sx={{ mt: 2 }}
                        />
                    )}
                </Box>
            )}
        </div>
    );

}
